import {Injectable, OnDestroy} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
  AppState,
  RouterStateUrl,
  successMicrositeSessions,
  track
} from '../../core.state';

import {Store} from '@ngrx/store';
import {
  createManager,
  failManager,
  failUpdateManager,
  followManager,
  getManager,
  getManagersList,
  getManagersListLoadMore,
  getManagersSearchListLoadMore,
  reloadManager,
  unfollowManager,
  removePinnedSyndicates,
  successCreateManager,
  successManager,
  successManagersList,
  successUpdateManager,
  updateManager
} from "../manager.actions";
import {catchError, delay, exhaustMap, filter, map, switchMap, takeUntil, tap, withLatestFrom} from "rxjs/operators";
import {ManagerClient} from "../../../client/manager";
import {of, Subject} from "rxjs";
import {routerNavigatedAction, routerNavigationAction} from '@ngrx/router-store';
import {selectManagersState} from './../manager.selectors';
import {callList, loadList} from '../../syndicates/syndicates.actions';

@Injectable()
export class ManagerRelatedEffects implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  routerNavigationManager = createEffect(() => {
    return this.actions$.pipe(
      ofType(routerNavigationAction),
      filter((routerProps) => !(routerProps.payload.routerState as unknown as RouterStateUrl).wallet),
      filter(propVal => (propVal.payload.event.url.includes('/manager') || propVal.payload.event.url.includes('/profile'))),
      map(propVal => (propVal.payload.routerState as unknown as RouterStateUrl)?.params?.manager_id),
      filter(managerId => !!managerId),
      takeUntil(this.destroy$),
      exhaustMap((managerId: string) => {
        return of(getManager({id: Number(managerId)}));
      }))
  }, {dispatch: true});

  routerNavigatedManager = createEffect(() => {
    return this.actions$.pipe(
      ofType(routerNavigatedAction),
      filter((routerProps) => !(routerProps.payload.routerState as unknown as RouterStateUrl).wallet),
      filter(propVal => (propVal.payload.event.url.includes('/manager') || propVal.payload.event.url.includes('/profile'))),
      map(propVal => (propVal.payload.routerState as unknown as RouterStateUrl)?.params?.manager_id),
      filter(managerId => !!managerId),
      takeUntil(this.destroy$),
      exhaustMap((managerId: string) => {
        return of(loadList({
          model: {
            filters: {
              managers: managerId
            }
          }
        }));
      }))
  }, {dispatch: true});

  getManagersList = createEffect(() => {
    return this.actions$.pipe(
      ofType(getManagersList),
      takeUntil(this.destroy$),
      switchMap((propVal) => {
        const params = {
          page: propVal.model.page || 1,
          per: propVal.model.per || 6,
          filters: {
            search: propVal.model.search,
            followed: propVal.model.followed,
          }
        }

        return this.managerClient.list(params).pipe(
          map(data => {
            return successManagersList({response: data, params: params});
          }),
          catchError(error => {
            console.error('[Manager Related Effects] Failed to load managers list:', error);
            return of(failManager({error: error}))
          })
        )
      })
    )
  });

  getManagersListLoadMore = createEffect(() => {
    return this.actions$.pipe(
      ofType(getManagersListLoadMore),
      withLatestFrom(this.store.select(selectManagersState)),
      takeUntil(this.destroy$),
      exhaustMap(([model, managersState]) => {
        // Pre-existing assumption: listParams is populated whenever this action fires — not independently verified
        return of(getManagersList({
          model: Object.assign({}, managersState.listParams,
            {page: managersState.listParams!.page + 1})
        }))
      })
    )
  });

  getManagersSearchListLoadMore = createEffect(() => {
    return this.actions$.pipe(
      ofType(getManagersSearchListLoadMore),
      withLatestFrom(this.store.select(selectManagersState)),
      takeUntil(this.destroy$),
      exhaustMap(([model, managersState]) => {
        return of(getManagersList({
          model: Object.assign({}, managersState.listParams,
            {page: model.model.page, search: model.search, followed: false})
        }))
      })
    )
  });

  reloadManager = createEffect(() => {
    return this.actions$.pipe(
      ofType(reloadManager),
      withLatestFrom(this.store.select(selectManagersState)),
      filter(([model, managersState]) => !!managersState.manager || !!managersState.my_manager),
      takeUntil(this.destroy$),
      exhaustMap(([model, managersState]) => {
        // check
        // Upstream filter guarantees manager or my_manager exists; assume it carries a real id
        return of(getManager({id: (managersState.manager?.id || managersState.my_manager?.id)!}));
      })
    )
  }, {dispatch: true});

  removePinnedSyndicates = createEffect(() => {
    return this.actions$.pipe(
      ofType(removePinnedSyndicates),
      withLatestFrom(this.store.select(selectManagersState)),
      takeUntil(this.destroy$),
      exhaustMap(([propVal, managersState]) => {
        // Pre-existing assumption: my_manager is populated whenever this action fires — not independently verified
        const manager = managersState.my_manager!;
        const payload: any = {
          id: manager.id,
          manager: {
            picture_url: manager.picture_url,
            twitter_username: manager.twitter_username,
            social_handles: manager.social_handles,
            favourite_syndicates: []
          }
        };
        return of(updateManager({model: payload}))
      })
    )
  });

  getManager = createEffect(() => {
    return this.actions$.pipe(
      ofType(getManager),
      takeUntil(this.destroy$),
      // Use exhaustMap to prevent duplicate concurrent requests
      exhaustMap((model: any) => {
        return this.managerClient.get(model.id).pipe(
          map(data => {
            return successManager({response: data});
          }),
          catchError(error => {
            console.error('[Manager Related Effects] Failed to load manager:', error);
            return of(failManager({error: error}))
          })
        )
      })
    )
  });

  successMicrositeSessions = createEffect(() => {
    return this.actions$.pipe(
      ofType(successMicrositeSessions),
      takeUntil(this.destroy$),
      exhaustMap((propVal) => {
        // response and manager_id are typed optional; not independently verified non-null here — pre-existing, unguarded call site
        return of(getManager({id: propVal.response!.manager_id!}));
      }))
  }, {dispatch: true});

  createManager = createEffect(() => {
    return this.actions$.pipe(
      ofType(createManager),
      takeUntil(this.destroy$),
      // Use exhaustMap to prevent duplicate manager creation
      exhaustMap((model: any) => {
        return this.managerClient.create(model).pipe(
          map(data => {
            // Dispatch side effect to reload manager data
            this.store.dispatch(getManager(data.id));
            return successCreateManager({response: data});
          }),
          catchError(error => {
            console.error('[Manager Related Effects] Failed to create manager:', error);
            return of(failManager({error: error}))
          })
        )
      })
    )
  });

  ///
  updateManager = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateManager),
      withLatestFrom(this.store.select(selectManagersState)),
      takeUntil(this.destroy$),
      // Use exhaustMap to prevent duplicate manager updates
      exhaustMap(([propVal, managersState]) => {
        // Pre-existing assumption: my_manager is populated whenever this action fires — not independently verified
        const updatePinnedSyndicates = managersState.my_manager!.pinnedSyndicates;

        return this.managerClient.update(Object.assign(
          {},
          propVal.model,
          {
            ...propVal.model,
            manager: {
              ...propVal.model.manager,
              favourite_syndicates: updatePinnedSyndicates?.length ? updatePinnedSyndicates
                : propVal.model.manager.favourite_syndicates ? propVal.model.manager.favourite_syndicates : []
            }
          })).pipe(
          map(data => successUpdateManager({response: data})),
          catchError(error => {
            console.error('[Manager Related Effects] Failed to update manager:', error);
            return of(failUpdateManager({error: error}))
          })
        )
      })
    )
  }, {dispatch: true});

  successUpdateManager = createEffect(() => {
    return this.actions$.pipe(
      ofType(successUpdateManager),
      delay(3000),
      takeUntil(this.destroy$),
      switchMap((model) => {
        return [
          getManager({id: model.response.id}),
          callList({})
        ]
      })
    )
  }, {dispatch: true});

  trackingSuccessUpdateManager = createEffect(() => {
    return this.actions$.pipe(
      ofType(successUpdateManager),
      withLatestFrom(this.store.select(selectManagersState)),
      takeUntil(this.destroy$),
      tap(([model, managersState]) => {
        // Pre-existing assumption: my_manager is populated whenever this action fires — not independently verified
        const my_manager = managersState.my_manager!;
        const eventsArray: any = [];
        if (my_manager.bio !== model.response.bio) {
          eventsArray.push({
            eventCategory: 'Manager',
            eventAction: 'Click',
            eventLabel: 'Bio',
            eventValue: my_manager.username
          })

        }

        if (my_manager.twitter_username !== model.response.twitter_username) {
          eventsArray.push({
            eventCategory: 'Manager',
            eventAction: 'Click',
            eventLabel: 'Twitter',
            eventValue: my_manager.username
          })
        }

        if ((my_manager.picture_url !== model.response.picture_url)/* || this.avatarCropped dontkno*/) {
          eventsArray.push({
            eventCategory: 'Manager',
            eventAction: 'Click',
            eventLabel: 'Avatar',
            eventValue: my_manager.username
          })
        }

        eventsArray.forEach((event: any) => {
          this.store.dispatch(track({event: event}));
        });

      })
    )
  }, {dispatch: false});
  ///
  followManager = createEffect(() => {
    return this.actions$.pipe(
      ofType(followManager),
      takeUntil(this.destroy$),
      // Use exhaustMap to prevent duplicate follow requests
      exhaustMap((action: any) =>
        this.managerClient.follow(action.id).pipe(
          map(response => {
            // Reload manager to get updated follow status
            return getManager({ id: action.id });
          }),
          catchError(error => {
            console.error('[Manager Related Effects] Failed to follow manager:', error);
            return of(failManager({error: error}))
          })
        )
      )
    )
  }, {dispatch: true});

  unfollowManager = createEffect(() => {
    return this.actions$.pipe(
      ofType(unfollowManager),
      takeUntil(this.destroy$),
      // Use exhaustMap to prevent duplicate unfollow requests
      exhaustMap((action: any) =>
        this.managerClient.unfollow(action.id).pipe(
          map(response => {
            // Reload manager to get updated follow status
            return getManager({ id: action.id });
          }),
          catchError(error => {
            console.error('[Manager Related Effects] Failed to unfollow manager:', error);
            return of(failManager({error: error}))
          })
        )
      )
    )
  }, {dispatch: true});

  constructor(
    public actions$: Actions,
    public store: Store<AppState>,
    public managerClient: ManagerClient
  ) {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
